/**
 * SettingsPanel - Full user settings editing panel
 * All users can edit their profile details, notification preferences, and upload logo.
 * Read-only for: id, role, permissions, username
 */
import React, { useState, useEffect, useRef } from 'react';
import axiosClient, { resolveStaticUrl } from '../api/axiosClient';
import { BRAND } from '../config/branding';

const TIMEZONE_OPTIONS = [
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
  'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Zurich', 'Europe/Vienna',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'America/Vancouver', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
  'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Lagos'
];

function SettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeSection, setActiveSection] = useState('profile');

  // Profile state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    phone: '',
    organisation_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    timezone: 'UTC',
  });

  // Read-only fields
  const [readOnly, setReadOnly] = useState({
    id: '',
    username: '',
    role: '',
    company_id: '',
    created_at: '',
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_enabled: true,
    login_alerts: true,
    task_assignments: true,
    task_reminders: true,
    certificate_expiry: true,
    mfa_alerts: true,
  });

  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ---------------------------------------------------------
  // LOAD SETTINGS
  // ---------------------------------------------------------
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axiosClient.get('/users/me/settings');
      const d = res.data;

      setProfile({
        first_name: d.first_name || '',
        last_name: d.last_name || '',
        full_name: d.full_name || '',
        email: d.email || '',
        phone: d.phone || '',
        organisation_name: d.organisation_name || '',
        address_line1: d.address_line1 || '',
        address_line2: d.address_line2 || '',
        city: d.city || '',
        state: d.state || '',
        postal_code: d.postal_code || '',
        country: d.country || '',
        timezone: d.timezone || 'UTC',
      });

      setReadOnly({
        id: d.id,
        username: d.username,
        role: d.role,
        company_id: d.company_id,
        created_at: d.created_at,
      });

      if (d.notification_preferences) {
        setNotifications(d.notification_preferences);
      }

      setLogoUrl(resolveStaticUrl(d.logo_url));
    } catch (err) {
      console.error('Failed to load settings:', err);
      showMessage('error', 'Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // SAVE PROFILE
  // ---------------------------------------------------------
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axiosClient.put('/users/me/settings', profile);

      // Update localStorage user object
      if (res.data.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }

      showMessage('success', res.data.message || 'Profile updated successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      showMessage('error', typeof detail === 'string' ? detail : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // SAVE NOTIFICATION PREFERENCES
  // ---------------------------------------------------------
  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axiosClient.put('/users/me/notification-preferences', notifications);
      showMessage('success', 'Notification preferences updated');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // LOGO UPLOAD
  // ---------------------------------------------------------
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Invalid file type. Allowed: PNG, JPG, JPEG, SVG.');
      e.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      showMessage('error', `File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds maximum (5 MB).`);
      e.target.value = '';
      return;
    }

    if (file.size === 0) {
      showMessage('error', 'Selected file is empty.');
      e.target.value = '';
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      const res = await axiosClient.post('/users/me/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLogoUrl(resolveStaticUrl(res.data.logo_url));
      setLogoPreview(null);
      setLogoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      showMessage('success', 'Logo uploaded successfully');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoClear = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const updateProfile = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const toggleNotification = (field) => {
    setNotifications(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getProfessionalRoleName = (role) => {
    const roleMap = {
      'third_party_admin': 'Certification Administrator',
      'super_admin': 'System Administrator',
      'client': 'Client User'
    };
    return roleMap[role] || role?.replace('_', ' ');
  };

  // ---------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------
  const cardStyle = {
    background: 'white',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '30px',
    marginBottom: '30px'
  };

  const labelStyle = {
    display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600'
  };

  const inputStyle = {
    width: '100%', padding: '12px', border: '1px solid #d4af37', borderRadius: '4px',
    fontSize: '14px', boxSizing: 'border-box', background: '#fff', color: '#1a1a1a'
  };

  const readOnlyStyle = {
    ...inputStyle, background: '#f8f8f8', color: '#666', cursor: 'not-allowed'
  };

  const btnPrimary = {
    padding: '12px 24px', background: '#d4af37', color: '#1a1a1a', border: 'none',
    borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  };

  const btnDisabled = { ...btnPrimary, background: '#ddd', color: '#999', cursor: 'not-allowed' };

  const sectionBtnStyle = (active) => ({
    padding: '10px 20px', border: 'none', borderRadius: '6px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', marginRight: '8px', marginBottom: '8px',
    background: active ? '#d4af37' : '#f0f0f0',
    color: active ? '#1a1a1a' : '#666'
  });

  // ---------------------------------------------------------
  // LOADING STATE
  // ---------------------------------------------------------
  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
        <p style={{ color: '#666' }}>Loading settings...</p>
      </div>
    );
  }

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------
  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, color: '#1a1a1a', fontSize: '32px', fontWeight: '700' }}>
          Account Settings
        </h1>
        <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
          Edit your profile, notification preferences, and organisation logo
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          padding: '15px', borderRadius: '6px', marginBottom: '20px',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: '14px'
        }}>
          {message.text}
        </div>
      )}

      {/* Section Tabs */}
      <div style={{ marginBottom: '24px' }}>
        <button style={sectionBtnStyle(activeSection === 'profile')} onClick={() => setActiveSection('profile')}>
          Profile
        </button>
        <button style={sectionBtnStyle(activeSection === 'address')} onClick={() => setActiveSection('address')}>
          Address
        </button>
        <button style={sectionBtnStyle(activeSection === 'notifications')} onClick={() => setActiveSection('notifications')}>
          Notifications
        </button>
        <button style={sectionBtnStyle(activeSection === 'logo')} onClick={() => setActiveSection('logo')}>
          Logo
        </button>
      </div>

      {/* ================================================================ */}
      {/* PROFILE SECTION */}
      {/* ================================================================ */}
      {activeSection === 'profile' && (
        <>
          {/* Read-only Account Info */}
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
              Account (Read-Only)
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={labelStyle}>Username</label>
                <input value={readOnly.username} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Role</label>
                <input value={getProfessionalRoleName(readOnly.role)} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>User ID</label>
                <input value={readOnly.id} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Member Since</label>
                <input value={readOnly.created_at ? new Date(readOnly.created_at).toLocaleDateString() : 'N/A'} readOnly style={readOnlyStyle} />
              </div>
            </div>
          </div>

          {/* Editable Profile */}
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
              Personal Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={labelStyle}>First Name</label>
                <input
                  value={profile.first_name}
                  onChange={(e) => updateProfile('first_name', e.target.value)}
                  placeholder="Enter first name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                <input
                  value={profile.last_name}
                  onChange={(e) => updateProfile('last_name', e.target.value)}
                  placeholder="Enter last name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Full Name (Display Name)</label>
                <input
                  value={profile.full_name}
                  onChange={(e) => updateProfile('full_name', e.target.value)}
                  placeholder="Enter display name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => updateProfile('email', e.target.value)}
                  placeholder="Enter email address"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => updateProfile('phone', e.target.value)}
                  placeholder="+44 7000 000000"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Organisation Name</label>
                <input
                  value={profile.organisation_name}
                  onChange={(e) => updateProfile('organisation_name', e.target.value)}
                  placeholder="Enter organisation name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Timezone</label>
                <select
                  value={profile.timezone}
                  onChange={(e) => updateProfile('timezone', e.target.value)}
                  style={inputStyle}
                >
                  {TIMEZONE_OPTIONS.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '25px', paddingTop: '25px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleSaveProfile} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ADDRESS SECTION */}
      {/* ================================================================ */}
      {activeSection === 'address' && (
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
            Address
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Address Line 1</label>
              <input
                value={profile.address_line1}
                onChange={(e) => updateProfile('address_line1', e.target.value)}
                placeholder="Street address, P.O. box, etc."
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Address Line 2</label>
              <input
                value={profile.address_line2}
                onChange={(e) => updateProfile('address_line2', e.target.value)}
                placeholder="Apartment, suite, unit, floor, etc."
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div>
              <label style={labelStyle}>City</label>
              <input
                value={profile.city}
                onChange={(e) => updateProfile('city', e.target.value)}
                placeholder="Enter city"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>State / Province / Region</label>
              <input
                value={profile.state}
                onChange={(e) => updateProfile('state', e.target.value)}
                placeholder="Enter state or province"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Postal / ZIP Code</label>
              <input
                value={profile.postal_code}
                onChange={(e) => updateProfile('postal_code', e.target.value)}
                placeholder="Enter postal code"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Country</label>
              <input
                value={profile.country}
                onChange={(e) => updateProfile('country', e.target.value)}
                placeholder="Enter country"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginTop: '25px', paddingTop: '25px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSaveProfile} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
              {saving ? 'Saving...' : 'Save Address'}
            </button>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* NOTIFICATIONS SECTION */}
      {/* ================================================================ */}
      {activeSection === 'notifications' && (
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
            Notification Preferences
          </h2>

          {[
            { key: 'email_enabled', label: 'Email Notifications', desc: 'Master switch for all email notifications' },
            { key: 'login_alerts', label: 'Login Alerts', desc: 'Receive alerts when your account is accessed from a new device' },
            { key: 'task_assignments', label: 'Task Assignments', desc: 'Receive notifications when tasks are assigned to you' },
            { key: 'task_reminders', label: 'Task Reminders', desc: 'Receive reminders for upcoming task deadlines' },
            { key: 'certificate_expiry', label: 'Certificate Expiry Warnings', desc: 'Be notified before certificates expire' },
            { key: 'mfa_alerts', label: 'MFA Alerts', desc: 'Receive alerts about MFA changes to your account' },
          ].map(item => (
            <div key={item.key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 0', borderBottom: '1px solid #f0f0f0'
            }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>{item.label}</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{item.desc}</div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifications[item.key]}
                  onChange={() => toggleNotification(item.key)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: '26px', transition: 'background 0.2s',
                  background: notifications[item.key] ? '#d4af37' : '#ccc'
                }}>
                  <span style={{
                    position: 'absolute', height: '20px', width: '20px', left: notifications[item.key] ? '24px' : '3px',
                    bottom: '3px', background: 'white', borderRadius: '50%', transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </span>
              </label>
            </div>
          ))}

          <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSaveNotifications} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* LOGO SECTION */}
      {/* ================================================================ */}
      {activeSection === 'logo' && (
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', marginBottom: '25px' }}>
            Organisation Logo
          </h2>

          {/* Logo Preview */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            {(logoPreview || logoUrl) ? (
              <img
                src={logoPreview || logoUrl}
                alt="Organisation Logo"
                style={{
                  maxWidth: '300px', maxHeight: '200px', border: '2px solid #d4af37',
                  borderRadius: '8px', padding: '8px', background: '#f8f8f8'
                }}
              />
            ) : (
              <div style={{
                width: '300px', height: '200px', border: '2px dashed #d4af37', borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#999', fontSize: '14px', margin: '0 auto', background: '#fafafa'
              }}>
                No logo uploaded
              </div>
            )}
          </div>

          {/* File Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ ...labelStyle, marginBottom: '10px' }}>
              Select Logo (PNG, JPG, JPEG, SVG - max 5 MB)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              onChange={handleLogoSelect}
              disabled={logoUploading}
              style={{
                width: '100%', padding: '12px', border: '2px solid #d4af37', borderRadius: '4px',
                fontSize: '14px', cursor: logoUploading ? 'not-allowed' : 'pointer', boxSizing: 'border-box'
              }}
            />
            {logoFile && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                Selected: <strong>{logoFile.name}</strong> ({(logoFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Upload / Clear Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleLogoClear}
              disabled={!logoFile || logoUploading}
              style={{
                padding: '12px 24px', fontSize: '14px', fontWeight: '600', color: '#666',
                background: '#f5f5f5', border: 'none', borderRadius: '6px',
                cursor: (!logoFile || logoUploading) ? 'not-allowed' : 'pointer',
                opacity: (!logoFile || logoUploading) ? 0.5 : 1
              }}
            >
              Clear
            </button>
            <button
              onClick={handleLogoUpload}
              disabled={!logoFile || logoUploading}
              style={(!logoFile || logoUploading) ? btnDisabled : btnPrimary}
            >
              {logoUploading ? 'Uploading...' : 'Upload Logo'}
            </button>
          </div>

          {/* Requirements */}
          <div style={{
            marginTop: '20px', padding: '12px', background: '#f8f8f8', borderRadius: '4px',
            fontSize: '13px', color: '#666'
          }}>
            <strong>Requirements:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>File format: PNG, JPG, JPEG, or SVG</li>
              <li>Maximum file size: 5 MB</li>
              <li>Recommended dimensions: 300x200 pixels or similar aspect ratio</li>
              <li>The logo will appear on your certificates and documents</li>
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px', borderTop: '1px solid #eee'
      }}>
        <p style={{ margin: 0 }}>{BRAND.name} Account Settings</p>
      </div>
    </div>
  );
}

export default SettingsPanel;
